import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Button } from 'Button';
import { Modal } from 'Modal';
import { Text } from 'Text';
import './locales/leave_project_group_confirmation_modal-en.json';

const Title = () => {
    const { t } = useTranslation('back_royal');
    return (
        <div className="flex items-center gap-2 text-primary">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t('leave_project_group_confirmation_modal.title')}
        </div>
    );
};

const Message = ({ className }: { className: string }) => {
    const { t } = useTranslation('back_royal');
    return (
        <Text color="black" className={twMerge('', className)}>
            {t('leave_project_group_confirmation_modal.message')}
        </Text>
    );
};

const ConfirmationButtons = ({
    onCancel,
    onConfirm,
    isLoading,
}: {
    onCancel: () => void;
    onConfirm: () => void;
    isLoading: boolean;
}) => {
    const { t } = useTranslation('back_royal');
    return (
        <div className="flex justify-end gap-3">
            <Button variant="light" height={8} onPress={onCancel} isDisabled={isLoading}>
                {t('leave_project_group_confirmation_modal.cancel_button_label')}
            </Button>
            <Button
                color="primary"
                height={8}
                onPress={onConfirm}
                isDisabled={isLoading}
                icon={isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : undefined}
            >
                {isLoading
                    ? t('leave_project_group_confirmation_modal.confirm_button_loading_label')
                    : t('leave_project_group_confirmation_modal.confirm_button_label')}
            </Button>
        </div>
    );
};

const ApiError = ({ className, apiError }: { className: string; apiError: string | null }) => {
    if (!apiError) return null;

    return <Text className={twMerge('text-center text-xs text-red-500', className)}>{apiError}</Text>;
};

export const LeaveProjectGroupConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    apiError,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
    apiError: string | null;
}) => {
    if (!isOpen) return null;

    return (
        <Modal
            classNames={{
                base: 'w-[400px] max-w-[90vw]',
                header: 'text-md font-semibold',
                wrapper: 'items-center sm:items-center',
                body: 'mx-7.5 mb-7.5',
            }}
            dismissOnEscape
            dismissOnOverlayClick
            onClose={onClose}
            scrollBehavior="outside"
            titleAlignment="start"
            title={<Title />}
        >
            <div data-testid="leave-project-group-confirmation-modal">
                <Message className="mb-6" />
                <ConfirmationButtons onCancel={onClose} onConfirm={onConfirm} isLoading={isLoading} />
                <ApiError className="mt-3" apiError={apiError} />
            </div>
        </Modal>
    );
};
